import type { ListBuilderConfiguration } from '../types';

/**
 * Routes list configuration (Phase A rename: was "route_template"; the
 * recurring-route template table is now just `route`).
 */
export const ROUTE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-routes',
			name: 'All Routes',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: [
				{
					id: 'route-name',
					headers: [
						{
							id: 'route-name-header',
							text: 'Route Name',
						},
					],
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
							link: {
								type: 'object',
								enabled: true,
							},
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					id: 'default-driver',
					headers: [
						{
							id: 'default-driver-header',
							text: 'Default Driver',
						},
					],
					fields: [
						{
							id: 'default-driver-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'defaultDriverName',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 150,
				},
				{
					id: 'default-vehicle',
					headers: [
						{
							id: 'default-vehicle-header',
							text: 'Default Vehicle',
						},
					],
					fields: [
						{
							id: 'default-vehicle-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'defaultVehicleName',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 150,
				},
				{
					id: 'default-warehouse',
					headers: [
						{
							id: 'default-warehouse-header',
							text: 'Default Warehouse',
						},
					],
					fields: [
						{
							id: 'default-warehouse-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'defaultWarehouse.name',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 150,
				},
				{
					id: 'stops-count',
					headers: [
						{
							id: 'stops-count-header',
							text: 'Default Stops',
						},
					],
					fields: [
						{
							id: 'stops-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'defaultStopsCount',
									objectTable: 'route',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 130,
				},
			],
			defaultSortId: 'route-name',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		// PD-13742: the Routes list defaults to the user's active warehouse but
		// stays a changeable chip. MY_WAREHOUSE resolves server-side to the user's
		// default; a user with no default sees all warehouses. Routes filter on
		// their default warehouse (route.defaultWarehouseId).
		activeFilters: ['tripWarehouse'],
		defaultFilters: {
			tripWarehouse: { id: 'MY_WAREHOUSE', label: 'My Warehouse' },
		},
	},
	// Sort definitions referenced by `defaultSortId` on each list above.
	// Without these the sort resolver logs "did not resolve to any sort
	// fields" — the column id alone is not enough; the resolver needs an
	// explicit field-direction mapping. Phase A note: the underlying
	// MySQL table is named `route_template` (legacy) even though the
	// TypeORM entity is now `RouteEntity`.
	tableSorts: [
		{
			id: 'route-name',
			name: 'Route Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'route',
				},
			],
			description: 'Sort routes by name alphabetically',
		},
	],
	objects: [
		{
			// SPA route lives at /distribution/route/:id (route templates are the
			// "Routes" landing post-Phase-A rename; the URL `/routes/...` is
			// dead). See modules/distribution/src/module.config.ts.
			baseUrl: '/distribution/route/:id',
			objectLabel: 'Route',
			objectTable: 'route',
			selectedFields: [
				{
					fieldName: '_id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'name',
					fieldLabel: 'Name',
				},
				{
					fieldName: 'description',
					fieldLabel: 'Description',
				},
				{
					fieldName: 'defaultDriverUserId',
					fieldLabel: 'Default Driver ID',
				},
				{
					fieldName: 'defaultVehicleId',
					fieldLabel: 'Default Vehicle ID',
				},
				{
					fieldName: 'defaultWarehouseId',
					fieldLabel: 'Default Warehouse ID',
				},
				{
					fieldName: 'defaultDriver',
					fieldLabel: 'Default Driver',
				},
				{
					fieldName: 'defaultDriverName',
					fieldLabel: 'Default Driver Name',
				},
				{
					fieldName: 'defaultVehicle',
					fieldLabel: 'Default Vehicle',
				},
				{
					fieldName: 'defaultVehicleName',
					fieldLabel: 'Default Vehicle Name',
				},
				{
					fieldName: 'defaultWarehouse',
					fieldLabel: 'Default Warehouse',
				},
				{
					fieldName: 'routeClients',
					fieldLabel: 'Route Stops',
				},
				{
					fieldName: 'defaultStopsCount',
					fieldLabel: 'Default Stops Count',
				},
				{
					fieldName: 'modifiedDate',
					fieldLabel: 'Modified Date',
				},
			],
		},
	],
};
